import React, { useEffect, useState, useCallback } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaEdit, FaHandshake, FaPlus, FaSearch, FaTrashAlt } from "react-icons/fa";
import { obtenerProveedores, guardarProveedor } from "../../../services/contabilidad/logisticaService";
import { logBusinessAction } from "../../../services/AuditService";

const BTN_GRADIENT = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const GestionProveedores = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingProveedor, setEditingProveedor] = useState({ ruc: "", razon_social: "", correo: "", telefono: "", direccion: "", contacto_nombre: "" });

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      const data = await obtenerProveedores(rucEmpresa);
      setProveedores(data);
    } catch (e) {
      setError("No se pudieron cargar los proveedores.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSave = async () => {
    if (!editingProveedor.ruc || !editingProveedor.razon_social) {
      setError("RUC y Razón Social son obligatorios.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const esEdicion = !!editingProveedor.id;
      await guardarProveedor(rucEmpresa, editingProveedor);

      // AUDITORÍA
      try {
        await logBusinessAction(
          esEdicion ? "EDICION_PROVEEDOR" : "CREACION_PROVEEDOR",
          "GestionCompras",
          {
            ruc: editingProveedor.ruc,
            razonSocial: editingProveedor.razon_social,
            contacto: editingProveedor.contacto_nombre
          },
          usuario
        );
      } catch (e) {
        console.warn("No se pudo registrar la auditoría del proveedor", e);
      }

      setSuccess("Proveedor guardado correctamente.");
      setShowModal(false);
      await cargar();
    } catch (e) {
      setError("Error al guardar el proveedor.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = proveedores.filter(p => 
    p.ruc.includes(searchTerm) || 
    p.razon_social.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="animate__animated animate__fadeIn">
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Barra de Búsqueda y Acciones */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  className="ps-5 border-0 bg-light rounded-pill py-2"
                  placeholder="Buscar proveedor por RUC o Razón Social..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button 
                style={BTN_GRADIENT} 
                className="rounded-pill px-4 shadow-sm"
                onClick={() => { setEditingProveedor({ ruc: "", razon_social: "", correo: "", telefono: "", direccion: "", contacto_nombre: "" }); setShowModal(true); }}
              >
                <FaPlus className="me-2" /> Nuevo Proveedor
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla de Proveedores */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">RUC / ID</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Razón Social</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Contacto</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Correo / Teléfono</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-5 text-muted">No se encontraron proveedores.</td></tr>
                ) : filtered.map((p) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td className="px-4 py-3 fw-bold text-dark">{p.ruc}</td>
                    <td className="px-4 py-3">
                      <div className="fw-semibold text-dark">{p.razon_social}</div>
                      <small className="text-muted">{p.direccion}</small>
                    </td>
                    <td className="px-4 py-3">{p.contacto_nombre || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="small">{p.correo || "—"}</div>
                      <div className="small text-muted">{p.telefono || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button variant="link" className="text-dark p-1 me-2" onClick={() => { setEditingProveedor(p); setShowModal(true); }}>
                        <FaEdit />
                      </Button>
                      <Button variant="link" className="text-danger p-1">
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* Modal de Creación/Edición */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white" }}>
          <Modal.Title className="fw-bold fs-5">
            <FaHandshake className="me-2" /> {editingProveedor.id ? "Editar Proveedor" : "Nuevo Proveedor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="small fw-bold text-muted">RUC</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.ruc} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, ruc: e.target.value})}
                  disabled={!!editingProveedor.id}
                />
              </Col>
              <Col md={8}>
                <Form.Label className="small fw-bold text-muted">Razón Social</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.razon_social} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, razon_social: e.target.value})}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">Nombre de Contacto</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.contacto_nombre} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, contacto_nombre: e.target.value})}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">Giro / Actividad</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.giro} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, giro: e.target.value})}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">Correo Electrónico</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.correo} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, correo: e.target.value})}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">Teléfono de Contacto</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  value={editingProveedor.telefono} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, telefono: e.target.value})}
                />
              </Col>
              <Col md={12}>
                <Form.Label className="small fw-bold text-muted">Dirección Fiscal</Form.Label>
                <Form.Control 
                  className="border-0 shadow-sm" 
                  as="textarea" rows={2}
                  value={editingProveedor.direccion} 
                  onChange={(e) => setEditingProveedor({...editingProveedor, direccion: e.target.value})}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light px-4 pb-4">
          <Button variant="link" className="text-muted text-decoration-none" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button style={BTN_GRADIENT} className="rounded-pill px-4" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Guardar Proveedor"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionProveedores;
